// run-command-release-flow-wiring.ts
//
// Bug G fix: the REAL `avatar autopilot run` factory for
// supervisor-mission-run.ts's `releaseFlowResumeInputFactory` — split out
// of run-command-cli-wiring.ts to keep that file small (same convention as
// every other run-command-*.ts split). Builds real deps (git rev-parse,
// PAT lookup, host detection) from `config.release`; caller (commands/
// autopilot.ts's `run` action, via run-command-cli-wiring.ts) only passes
// this when `config.release` is set — undefined config.release already
// short-circuits inside maybeResumeReleaseFlowAfterDone regardless, this
// factory existing or not is otherwise harmless either way.
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { isGithubHost, parseGitRemoteHost } from "../git-remote-host.js";
import { lookupGitlabPat } from "./gitlab-pat-lookup.js";
import type { ReleaseSecretsStdin } from "./release-secrets-stdin-reader.js";
import type { WorkerReviewReport } from "./review-local-adapter.js";
import { mergeReviewReportVerdicts } from "./review-report-merge.js";
import { reviewStagingFileName } from "./review-report-promotion.js";
import { REVIEW_REPORT_REQUIRED_ADAPTER_IDS } from "./review-report-promotion-wiring.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";
import { realGitRunner } from "./supervisor-git-runner.js";
import { buildSupervisorPolicyEvaluator } from "./supervisor-policy.js";
import type { ResumeReleaseFlowInput } from "./supervisor-release-flow-resume.js";

export interface BuildReleaseFlowResumeInputArgs {
  repoDir: string;
  controlRoot: string;
  missionId: string;
  config: AutopilotConfig;
  /** Phase 04b: `run --release-secrets-stdin` map — passed through in memory only. */
  releaseSecretsStdin?: ReleaseSecretsStdin;
}

/**
 * Review report staging convention (Bug G): a worker's `review-local-v1`
 * ticket (not yet auto-seeded by mission-initializer-tickets.ts — separate,
 * still-open gap, documented in phase-14 evidence) would write here. Absent
 * file => resumeReleaseFlowIfConfigured returns "waiting_review_report",
 * never a fabricated risk value.
 */
function reviewStagingPath(controlRoot: string, candidateSha: string, adapterId?: string): string {
  return join(controlRoot, "review-staging", reviewStagingFileName(candidateSha, adapterId));
}

/**
 * Code review P3 L2: a failed rev-parse THROWS. Returning "" made an empty
 * candidate that matches no G3, so CXR-03 withdrew a good G3 on a git error.
 */
async function revParse(repoDir: string, ref: string): Promise<string> {
  const res = await realGitRunner(["rev-parse", "--verify", "--end-of-options", ref], repoDir);
  const sha = res.stdout.trim();
  if (res.status !== 0 || sha === "") {
    throw new Error(
      `git rev-parse ${ref} thất bại trong ${repoDir} (status ${res.status}): ${res.stderr.trim()}`,
    );
  }
  return sha;
}

async function readCandidateAndBaseSha(
  repoDir: string,
  targetBranch: string,
): Promise<{ candidateSha: string; baseSha: string }> {
  return {
    candidateSha: await revParse(repoDir, "HEAD"),
    baseSha: await revParse(repoDir, targetBranch),
  };
}

async function readOneReviewReport(
  controlRoot: string,
  candidateSha: string,
  adapterId?: string,
): Promise<WorkerReviewReport | undefined> {
  try {
    const raw = await readFile(reviewStagingPath(controlRoot, candidateSha, adapterId), "utf8");
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as WorkerReviewReport)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * H1 nửa B: đọc verdict của MỌI reviewer đã promote cho candidate này rồi gộp
 * theo luật "risk cao nhất thắng" (`review-report-merge.ts`).
 *
 * Gộp ở tầng ĐỌC chứ không phải tầng ghi: mỗi reviewer vẫn có file riêng nên
 * truy được ai nói gì, và mission chỉ có một reviewer — mọi mission trước thay
 * đổi này, mọi fixture e2e — đi qua nhánh `length === 1` của hàm gộp, trả
 * nguyên văn report cũ. Không report nào ⇒ `undefined` ⇒
 * `waiting_review_report`, không bịa risk.
 */
async function readWorkerReviewReport(
  controlRoot: string,
  repoDir: string,
): Promise<WorkerReviewReport | undefined> {
  const candidateSha = await revParse(repoDir, "HEAD");
  const reports = await Promise.all(
    REVIEW_REPORT_REQUIRED_ADAPTER_IDS.map((adapterId) =>
      readOneReviewReport(controlRoot, candidateSha, adapterId),
    ),
  );
  return mergeReviewReportVerdicts(reports.filter((r): r is WorkerReviewReport => r !== undefined));
}

/** Builds the deferred-I/O factory `runMissionToIdle` calls only after ticket-loop "done". */
export function buildReleaseFlowResumeInputFactory(
  args: BuildReleaseFlowResumeInputArgs,
): () => Promise<
  Omit<ResumeReleaseFlowInput, "store" | "controlRoot" | "missionId" | "repoDir" | "config">
> {
  return async () => {
    const release = args.config.release;
    if (!release) {
      // Guarded by the caller (config.release presence) but kept fail-safe:
      // an empty pushInput/remoteEvidenceInput would only ever be read if
      // resumeReleaseFlowIfConfigured's own `!config.release` short-circuit
      // is bypassed some day — never silently proceed with placeholders.
      throw new Error("buildReleaseFlowResumeInputFactory gọi khi config.release chưa cấu hình.");
    }
    const remoteUrlResult = await realGitRunner(
      ["remote", "get-url", release.remote],
      args.repoDir,
    );
    const remoteUrl = remoteUrlResult.stdout.trim();
    const host = parseGitRemoteHost(remoteUrl) ?? "";
    const branch = `candidate/${args.missionId}`;
    // H14 bản 2 (2026-09-29): deploy/verify inputs and the release manifest
    // hash are no longer built here. They used to fall back to `["true"]`
    // for a missing recipe, self-match `observedRunningSha` with the
    // candidate and bind G3 to the constant `${missionId}-${branch}` — so a
    // mission with no deploy recipe journaled a "succeeded" deploy + verify
    // and a `deployed_verified` receipt. resumeReleaseFlowIfConfigured now
    // derives all three from `config.release` itself (one source of truth).

    return {
      readCandidateAndBaseSha: () => readCandidateAndBaseSha(args.repoDir, release.target_branch),
      readCandidateSha: () => revParse(args.repoDir, "HEAD"),
      readWorkerReviewReport: () => readWorkerReviewReport(args.controlRoot, args.repoDir),
      pushInput: {
        gitRunner: realGitRunner,
        evaluatePolicy: buildSupervisorPolicyEvaluator(),
        remote: release.remote,
        branch,
        target: release.target_branch,
        // Bare controlRoot — readReviewReceipt/writeReviewReceipt
        // (review-state-store.ts) both derive `<root>/review-state/...`
        // internally via their OWN reviewStateRoot() helper; passing an
        // already-joined "<controlRoot>/review-state" here double-nests it
        // to "<controlRoot>/review-state/review-state/..." and the real
        // policy evaluator (buildSupervisorPolicyEvaluator, not the fixture
        // stub used elsewhere) never finds the receipt writeReviewReceipt
        // just wrote — found via this factory being the first REAL (non-
        // stubbed evaluatePolicy) caller of pushDraftMergeRequest.
        reviewStateRoot: args.controlRoot,
        deployRecipeId: release.deploy_recipe?.recipe_id ?? null,
      },
      remoteEvidenceInput: {
        host,
        remoteUrl,
        sourceBranch: branch,
        baseBranch: release.target_branch,
        repoDir: args.repoDir,
      },
      remoteEvidenceDeps: isGithubHost(host)
        ? undefined
        : {
            gitlabClientDeps: {
              lookupPat: () => lookupGitlabPat(host),
              // P18: until now nothing could reach the client's `baseUrl`
              // override, so it always fell back to `https://<host>` — which
              // made the Draft-MR/REVIEW_REMOTE/G3 leg impossible to run
              // against a local fake GitLab, and gave a self-hosted GitLab on
              // a separate API origin no way to say so. `undefined` keeps the
              // old fallback exactly.
              baseUrl: release.gitlab_base_url,
            },
          },
      policyDigest: "release-flow-cli-wiring-v1",
      adapterDigest: "release-flow-cli-wiring-v1",
      eventSeq: 1,
      actorId: "run-command-cli",
      releaseSecretsStdin: args.releaseSecretsStdin,
    };
  };
}
